/**
 * ApprovalManager Dialogs — Detail/Action and Config dialogs
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';

export const ApprovalDetailDialog = ({
  request, onClose, onAction, actionLoading, actionNote, setActionNote,
  canApprove, userId, categoryLabels, priorityColors
}) => (
  <Dialog open={!!request} onOpenChange={() => onClose()}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Approval Request Details</DialogTitle>
        <DialogDescription>Review the proposed changes before taking action</DialogDescription>
      </DialogHeader>
      {request && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-muted-foreground">Category</Label><p className="font-medium">{categoryLabels[request.category]}</p></div>
            <div><Label className="text-muted-foreground">Priority</Label><Badge className={priorityColors[request.priority]}>{request.priority}</Badge></div>
            <div><Label className="text-muted-foreground">Action</Label><p className="font-medium capitalize">{request.action}</p></div>
            <div><Label className="text-muted-foreground">Resource</Label><p className="font-medium">{request.resource_type}</p></div>
          </div>
          <div><Label className="text-muted-foreground">Description</Label><p>{request.description || 'No description provided'}</p></div>
          <div>
            <Label className="text-muted-foreground">Proposed Changes</Label>
            <pre className="bg-muted p-3 rounded-md text-sm overflow-auto max-h-48">{JSON.stringify(request.changes, null, 2)}</pre>
          </div>
          {request.status === 'pending' && canApprove(request) && (
            <div>
              <Label>Add a note (optional)</Label>
              <Textarea value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="Reason for approval or rejection..." rows={2} />
            </div>
          )}
          {request.status === 'pending' && request.requested_by === userId && (
            <Alert><AlertTriangle className="h-4 w-4" /><AlertDescription>You cannot approve your own requests. Another admin must review.</AlertDescription></Alert>
          )}
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        {request?.status === 'pending' && canApprove(request) && (
          <>
            <Button variant="destructive" onClick={() => onAction(request.id, 'reject')} disabled={actionLoading === request?.id}>
              {actionLoading === request?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />} Reject
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => onAction(request.id, 'approve')} disabled={actionLoading === request?.id}>
              {actionLoading === request?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />} Approve & Apply
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ApprovalConfigDialog = ({
  open, onOpenChange, config, setConfig, onSave, categoryLabels
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Dual Approval Configuration</DialogTitle>
        <DialogDescription>Configure which changes require dual approval</DialogDescription>
      </DialogHeader>
      {config && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div><Label>Enable Dual Approval</Label><p className="text-sm text-muted-foreground">Require second admin approval for critical changes</p></div>
            <Switch checked={config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} />
          </div>
          <div className="flex items-center justify-between">
            <div><Label>Critical Requires Sysadmin</Label><p className="text-sm text-muted-foreground">Only sysadmins can approve critical priority changes</p></div>
            <Switch checked={config.critical_requires_sysadmin} onCheckedChange={(v) => setConfig({ ...config, critical_requires_sysadmin: v })} />
          </div>
          <div>
            <Label>Default Expiration (hours)</Label>
            <Input type="number" value={config.default_expiration_hours} onChange={(e) => setConfig({ ...config, default_expiration_hours: parseInt(e.target.value) || 48 })} min={1} max={168} />
          </div>
          <div>
            <Label>Categories Requiring Approval</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {Object.entries(categoryLabels).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <Switch checked={config.categories_requiring_approval?.includes(key)} onCheckedChange={(checked) => {
                    const cats = config.categories_requiring_approval || [];
                    setConfig({ ...config, categories_requiring_approval: checked ? [...cats, key] : cats.filter(c => c !== key) });
                  }} />
                  <span className="text-sm">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSave}>Save Configuration</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
